import { dasherize } from '@ember/string';
import {
  generateValidationsFromMetadata,
  validateArgumentList,
} from '../../../utils/component-validations';
import { guidFor } from '@ember/object/internals';
import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import { modifier } from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';
import { runInDebug } from '@ember/debug';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';

const METADATA = {
  meta: {
    id: {
      type: 'string',
      description: 'Optional identifier; overrides automated ID generation',
      optional: true,
    },
    registerComponent: {
      type: 'action',
      description: 'Function to register component with parent',
      optional: true,
    },
    unregisterComponent: {
      type: 'action',
      description: 'Function to unregister component from parent',
      optional: true,
    },
  },
};
const STYLE_ATTRIBUTES = ['baseStyles', 'styles', 'variantStyles'];

/**
 * Base class for SPDS components
 *
 * Arguments
 * @argument {string} id optional identifier; overrides automated ID generation
 * @argument {function} registerComponent allows registration of component with parent
 * @argument {function} unregisterComponent remove registration of component with parent
 *
 * @property {array} arguments an array of validation objects that validate the arguments for the inheriting component
 * @property {string} baseClass the base CSS class for the component; fallback is `spds-component`
 * @property {array|getter} classesToMapToArgs an array (or getter) of strings that list argument names which should be conditionally converted to CSS classes based on their value
 * @property {array|getter} classesToMapToAttrs an array (or getter) of strings that list attribute names which should be conditionally converted to CSS classes based on their value
 * @property {array|getter} propsToMapToArgs an array (or getter) of strings that list argument names which should be converted to CSS custom properties
 * @property {array|getter} propsToMapToAttrs an array (or getter) of strings that list attribute names which should be converted to CSS custom properties
 * @property {string} componentName an optional name for validation errors to reference for the component; defaults to this.constructor.name; provided by inheriting components
 */
export default class SpdsCoreBase extends Component {
  static metadata = METADATA;
  #setupRun = false;

  // fallbacks in case attrs not provided in inheriting component
  arguments = [];
  baseClass = 'spds-component';

  @tracked baseElement;
  @tracked uniqueIdentifier;

  /**
   * allows for dynamically updating classes on component
   * based on argument or attribute values
   * inheriting components can override the
   * `classesToMapToArgs` and `classesToMapToAttrs` getters
   * @return {string} the processed CSS classes to be applied to the DOM element
   */
  get classes() {
    let classes = [this.styling, this.baseClass, this.variantClass, this.additionalClasses];

    [...(this.classesMappedToArgs || []), ...(this.classesMappedToAttrs || [])].forEach(obj => {
      if (typeof obj.arg === 'boolean' && obj.arg) {
        classes.push(obj.class);
      } else if (typeof obj.arg === 'string' && !isEmpty(obj.arg)) {
        classes.push(obj.arg);
      }
    });

    return classes.filter(Boolean).join(' ');
  }

  /**
   * map CSS classes to arguments passed into the component
   * @return {array} array of objects mapping CSS classes and matching values
   */
  get classesMappedToArgs() {
    return this.#mapClassesToValues(this.classesToMapToArgs, this.args);
  }

  /**
   * map CSS classes to attributes defined on the component
   * @return {array} array of objects mapping CSS classes and matching values
   */
  get classesMappedToAttrs() {
    return this.#mapClassesToValues(this.classesToMapToAttrs, this);
  }

  get classesToMapToArgs() {
    return [];
  }

  get classesToMapToAttrs() {
    return [];
  }

  /**
   * generate inline style string with CSS custom properties
   * based on argument or attribute values
   * @return {string|null} the inline styles to be applied to the DOM element, or null if none
   */
  get customProperties() {
    let props = [...(this.propsMappedToArgs || []), ...(this.propsMappedToAttrs || [])];

    if (!props.length) return null;

    return htmlSafe(
      props
        .filter(obj => !isEmpty(obj.value))
        .map(obj => `${obj.prop}: ${obj.value}`)
        .join('; ')
    );
  }

  /**
   * generate ID for component based on unique identifier generated in constructor
   * @return {string} the composed id, or the @id argument value
   */
  get id() {
    let { id } = this.args;
    let { baseClass, uniqueIdentifier } = this;

    return id ?? `${baseClass}-${uniqueIdentifier}`;
  }

  /**
   * map CSS custom properties to arguments passed into the component
   * @return {array} array of objects mapping CSS custom properties and matching values
   */
  get propsMappedToArgs() {
    return this.#mapPropsToValues(this.propsToMapToArgs, this.args);
  }

  /**
   * map CSS custom properties to attributes defined on the component
   * @return {array} array of objects mapping CSS custom properties and matching values
   */
  get propsMappedToAttrs() {
    return this.#mapPropsToValues(this.propsToMapToAttrs, this);
  }

  get propsToMapToArgs() {
    return [];
  }

  get propsToMapToAttrs() {
    return [];
  }

  /**
   * allows for assigning some styles to a base component as `baseStyles` attribute
   * and inheriting component can assign its own styles based on `styles` attribute
   * @return {string} the styles imported from SCSS modules, processed into strings
   */
  get styling() {
    return STYLE_ATTRIBUTES.map(style => this[style]?.component ?? '')
      .filter(Boolean)
      .join(' ');
  }

  constructor() {
    super(...arguments);

    // used to generate DOM ID for component
    // by default generates a string formatted like `ember123`
    // this reduces it to just the numeric portion of the generated ID
    this.uniqueIdentifier = guidFor(this).replace(/[^0-9]/g, '');

    // this powers the ability to register a subcomponent with its parent
    // parent must define all behavior around how to handle registration/unregistration
    if (this.args.registerComponent) {
      this.args.registerComponent?.(this);

      // automatically unregister the component from the parent on destruction
      registerDestructor(this, () => {
        this.args.unregisterComponent?.(this.id);
      });
    }

    // Base class always adds its own metadata validations
    this.arguments = generateValidationsFromMetadata(METADATA);
  }

  /**
   * compose argument validations for component based on provided metadata
   * Adds child class metadata validations to existing base class validations.
   * @param {object} metadata the metadata object defining the arguments for the component
   * @returns {array} array of validation objects for the component arguments
   */
  composeArguments(metadata) {
    return [...this.arguments, ...generateValidationsFromMetadata(metadata)];
  }

  /**
   * Generates a unique ID for subcomponents by appending a suffix to the current component's ID.
   * This ensures that subcomponents have unique and predictable DOM IDs.
   *
   * @param {string} suffix - The suffix to append to the current component's ID.
   * @returns {string} - The composed subcomponent ID in the format: `<component-id>-<suffix>`.
   */
  composeSubcomponentId(suffix) {
    return [this.id, '-', suffix].join('');
  }

  /**
   * generate array of objects that determine if mapped CSS class should be
   * applied based on arg or attr value
   * @param {Array<string>} classes array fo strings representing CSS class names to map to value source
   * @param {object} valueSource the object which should be used to find values matching the CSS class array
   * @returns
   * {
   *   arg: string or boolean,
   *   class: string // dasherized version of string from classes array
   * }
   */
  #mapClassesToValues(classes, valueSource) {
    return classes
      .filter(c => valueSource[c])
      .map(c => ({ arg: valueSource[c], class: dasherize(c) }));
  }

  /**
   * generate array of objects that determine if mapped CSS custom property should be
   * applied based on arg or attr value
   * @param {Array<string>} props array of strings representing CSS custom property names to map to value source
   * @param {object} valueSource the object which should be used to find values matching the props array
   * @returns {Array<object>}
   * {
   *   value: string or number,
   *   prop: string // CSS custom property name with -- prefix and dasherized
   * }
   */
  #mapPropsToValues(props, valueSource) {
    return props
      .filter(p => valueSource[p])
      .map(p => ({ value: valueSource[p], prop: `--${dasherize(p)}` }));
  }

  /**
   * modifier to register DOM elements for component
   * should be called on container for component to properly set `baseElement` and query DOM
   * set `elementsToRegister` to be an array on component that inherits from this class
   * each array item should be an object with `name` and `selector` attributes
   * element is registered to `this[name]` based on DOM query within component baseElement
   */
  #registerElements = element => {
    this.baseElement = element;

    this.elementsToRegister?.forEach(({ name, selector }) => {
      let el = element.querySelector(selector);

      if (el) this[name] = el;
    });
  };

  setupComponent = modifier(element => {
    if (this.#setupRun) return;

    this.#registerElements(element);

    this.setup?.();

    this.#setupRun = true;
  });

  // validate provided arguments for component
  validateArguments = modifier(() => {
    runInDebug(() => {
      if (!this.arguments) return;

      let { args, componentName, constructor, arguments: argumentList } = this;

      validateArgumentList({ args, argumentList, component: componentName || constructor.name });
    });
  });

  /**
   * set this[name] to ref for child component
   * must be called using the `fn` helper and passing name
   * ex: {{fn this.registerComponent "labelComponent"}}
   */
  registerComponent = (name, component) => {
    this[name] = component;
  };

  /**
   * remove ref for child component
   * must be called using `fn` helper and passing name
   * ex: {{fn this.unregisterComponent "labelComponent"}}
   */
  unregisterComponent = name => {
    this[name] = null;
  };
}

export const BASE_METADATA = METADATA;
